import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SparklesIcon, PlayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { apiClient } from '@/services/api'
import { clsx } from 'clsx'
import OrchestratorResultPanel from './OrchestratorResultPanel'
import { useOrchestratorStream } from './useOrchestratorStream'

const RECENT_KEY = 'orchestrator.recentPrompts'
const MAX_RECENT = 5

export interface ComposerHandle {
  focus: () => void
  setPrompt: (next: string) => void
}

type Mode = 'auto' | 'pick'

const OrchestratorComposer = forwardRef<ComposerHandle>((_, ref) => {
  const [prompt, setPrompt] = useState('')
  const [mode, setMode] = useState<Mode>('auto')
  const [pickedAgent, setPickedAgent] = useState<string>('')
  const [contextOpen, setContextOpen] = useState(false)
  const [contextJson, setContextJson] = useState('{}')
  const [recent, setRecent] = useState<string[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { state, run, reset } = useOrchestratorStream()
  const isRunning = state.phase !== 'idle' && state.phase !== 'done' && state.phase !== 'error'

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    setPrompt: (next: string) => {
      setPrompt(next)
      // Defer focus until after the controlled value updates so the cursor
      // lands at the end of the injected text.
      setTimeout(() => textareaRef.current?.focus(), 0)
    },
  }))

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY)
      if (raw) setRecent(JSON.parse(raw))
    } catch {
      // ignore
    }
  }, [])

  const { data: agents = [] } = useQuery({
    queryKey: ['centralized-agents'],
    queryFn: () => apiClient.getCentralizedAgents({}),
    staleTime: 60_000,
    enabled: mode === 'pick',
  })

  const submit = () => {
    const trimmed = prompt.trim()
    if (!trimmed || isRunning) return
    let ctx: Record<string, any> = {}
    try { ctx = contextJson.trim() ? JSON.parse(contextJson) : {} }
    catch { return }

    const agentName = mode === 'pick' && pickedAgent ? pickedAgent : 'auto-orchestrator'
    run(trimmed, ctx, agentName)

    // persist recent
    const next = [trimmed, ...recent.filter((p) => p !== trimmed)].slice(0, MAX_RECENT)
    setRecent(next)
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  const onClear = () => {
    reset()
    setPrompt('')
  }

  return (
    <Card className="relative" padding="md">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <SparklesIcon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-semibold text-foreground mb-2">
            What do you want to accomplish?
          </label>
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={'e.g. "Analyze Q2 project health and draft an update email for the exec team"'}
            rows={3}
            disabled={isRunning}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            data-testid="orchestrator-composer-prompt"
          />

          <div className="mt-3 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 text-sm">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="orch-mode"
                  value="auto"
                  checked={mode === 'auto'}
                  onChange={() => setMode('auto')}
                  disabled={isRunning}
                  className="accent-primary"
                />
                <span className={clsx(mode === 'auto' ? 'text-foreground' : 'text-muted-foreground')}>
                  Auto-route <span className="text-xs text-muted-foreground">(recommended)</span>
                </span>
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="orch-mode"
                  value="pick"
                  checked={mode === 'pick'}
                  onChange={() => setMode('pick')}
                  disabled={isRunning}
                  className="accent-primary"
                />
                <span className={clsx(mode === 'pick' ? 'text-foreground' : 'text-muted-foreground')}>
                  Pick an agent
                </span>
              </label>
              {mode === 'pick' && (
                <select
                  value={pickedAgent}
                  onChange={(e) => setPickedAgent(e.target.value)}
                  disabled={isRunning}
                  className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid="orchestrator-composer-agent"
                >
                  <option value="">Select agent…</option>
                  {(agents as any[]).slice(0, 30).map((a) => (
                    <option key={a.name} value={a.name}>{a.name}</option>
                  ))}
                </select>
              )}
            </div>

            <button
              type="button"
              onClick={() => setContextOpen((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {contextOpen ? 'Hide' : 'Context (JSON) — optional'}
            </button>

            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground hidden sm:inline">
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
              </span>
              {(state.phase === 'done' || state.phase === 'error') && (
                <Button variant="ghost" size="sm" onClick={onClear}>
                  <XMarkIcon className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
              <Button
                variant="primary"
                size="md"
                onClick={submit}
                isLoading={isRunning}
                disabled={!prompt.trim() || (mode === 'pick' && !pickedAgent) || isRunning}
                data-testid="orchestrator-composer-run"
              >
                <PlayIcon className="w-4 h-4 mr-1" />
                Run
              </Button>
            </div>
          </div>

          {contextOpen && (
            <textarea
              value={contextJson}
              onChange={(e) => setContextJson(e.target.value)}
              rows={3}
              disabled={isRunning}
              placeholder='{ "projectId": "..." }'
              className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
            />
          )}

          {recent.length > 0 && state.phase === 'idle' && !prompt && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="text-[11px] text-muted-foreground mr-1">Recent:</span>
              {recent.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPrompt(p)}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground truncate max-w-[240px]"
                  title={p}
                >
                  {p.length > 40 ? p.slice(0, 37) + '…' : p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {state.phase !== 'idle' && (
        <div className="mt-4">
          <OrchestratorResultPanel state={state} />
        </div>
      )}
    </Card>
  )
})

OrchestratorComposer.displayName = 'OrchestratorComposer'
export default OrchestratorComposer
